import { useState } from "react";
import { getFlagUrl } from "@/lib/flagUtils";
import { Check, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { COUNTRIES } from "@/data/countries";
export type { Country } from "@/data/countries";
export { COUNTRIES } from "@/data/countries";

interface CountryMultiSelectProps {
  value: string[];
  onChange: (countries: string[]) => void;
  maxSelections?: number;
}

const CountryMultiSelect = ({ value, onChange, maxSelections }: CountryMultiSelectProps) => {
  const [open, setOpen] = useState(false);

  const toggleCountry = (countryName: string) => {
    if (value.includes(countryName)) {
      onChange(value.filter((c) => c !== countryName));
    } else {
      if (maxSelections && value.length >= maxSelections) {
        return;
      }
      onChange([...value, countryName]);
    }
  };

  const removeCountry = (countryName: string) => {
    onChange(value.filter((c) => c !== countryName));
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-start text-left font-normal"
          >
            {value.length === 0 ? (
              <span className="text-muted-foreground">Sélectionner des pays...</span>
            ) : (
              <span>{value.length} pays sélectionné{value.length > 1 ? "s" : ""}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder="Rechercher un pays..." />
            <CommandEmpty>Aucun pays trouvé.</CommandEmpty>
            <CommandGroup>
              <ScrollArea className="h-[300px]">
                {COUNTRIES.map((country) => (
                  <CommandItem
                    key={country.code}
                    value={country.name}
                    onSelect={() => toggleCountry(country.name)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value.includes(country.name) ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {getFlagUrl(country.name) ? (
                      <img src={getFlagUrl(country.name)!} alt="" className="mr-2 h-[15px] w-[20px] object-cover rounded-[2px]" loading="lazy" />
                    ) : (
                      <span className="mr-2 text-lg">🌍</span>
                    )}
                    {country.name}
                  </CommandItem>
                ))}
              </ScrollArea>
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {value.map((countryName) => {
            const country = COUNTRIES.find((c) => c.name === countryName);
            return (
              <Badge key={countryName} variant="secondary" className="gap-1">
                {country && getFlagUrl(countryName) ? (
                  <img src={getFlagUrl(countryName)!} alt="" className="h-[12px] w-[16px] object-cover rounded-[1px]" />
                ) : null}
                {countryName}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={() => removeCountry(countryName)}
                />
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CountryMultiSelect;
